import React, { useEffect, useMemo } from 'react';
import type {
  AlgorithmicAggregation,
  AlgorithmicUnit,
  EvaluationDirection,
  EvaluationMode,
  EvaluationScope,
  EvaluatorParamsFormState,
  LlmJudgeMetricId,
  AlgorithmicMetricId,
} from '../types/evaluation';
import {
  ALGORITHMIC_METRIC_LABELS,
  ALGORITHMIC_METRIC_OPTIONS,
  BERT_SCORE_LANG_LABELS,
  BERT_SCORE_LANG_OPTIONS,
  BERT_SCORE_METRIC_LABELS,
  BERT_SCORE_METRIC_OPTIONS,
  BERT_SCORE_MODEL_LABELS,
  BERT_SCORE_MODEL_OPTIONS,
  LLM_JUDGE_METRIC_LABELS,
  LLM_JUDGE_METRIC_OPTIONS,
  OPENAI_SIMILARITY_EMBEDDING_OPTIONS,
  SIMILARITY_EMBEDDING_OPTIONS,
  evaluationModeHasEvaluatorParams,
} from '../types/evaluation';
import type { BertScoreLang, BertScoreMetric, BertScoreModelType } from '../types/evaluation';
import { mergeConfiguratorModelOptions } from '../utils/configuratorModels';
import './EvaluatorParamsFields.css';

export interface EvaluatorParamsFieldsProps {
  mode: EvaluationMode;
  value: EvaluatorParamsFormState;
  onChange: (next: EvaluatorParamsFormState) => void;
  disabled?: boolean;
  /** Config tuning (runner) includes direction for candidate selection. */
  includeDirection?: boolean;
  idPrefix?: string;
  /** Integration models for LLM judge picker (Profile → Models / configurator). */
  availableModels?: string[];
}

function fieldId(prefix: string, name: string): string {
  return `${prefix}-${name}`;
}

const EvaluatorParamsFields: React.FC<EvaluatorParamsFieldsProps> = ({
  mode,
  value,
  onChange,
  disabled = false,
  includeDirection = false,
  idPrefix = 'evaluator-params',
  availableModels: availableModelsProp,
}) => {
  const patch = (partial: Partial<EvaluatorParamsFormState>) => onChange({ ...value, ...partial });

  const modelOptions = useMemo(
    () => mergeConfiguratorModelOptions(availableModelsProp ?? []),
    [availableModelsProp]
  );

  useEffect(() => {
    if (mode !== 'llm-judge' || modelOptions.length === 0) return;
    if (value.judge_model_name && modelOptions.includes(value.judge_model_name)) return;
    onChange({ ...value, judge_model_name: modelOptions[0] });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync default judge model when list changes
  }, [mode, modelOptions, value.judge_model_name]);

  useEffect(() => {
    if (mode !== 'algorithmic' || modelOptions.length === 0) return;
    if (value.token_model_name && modelOptions.includes(value.token_model_name)) return;
    onChange({ ...value, token_model_name: modelOptions[0] });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync default token model when list changes
  }, [mode, modelOptions, value.token_model_name]);

  const judgeTemperature = Number.isFinite(value.judge_temperature) ? value.judge_temperature : 0;

  if (!evaluationModeHasEvaluatorParams(mode)) {
    return null;
  }

  return (
    <div className="evaluator-params">
      {mode === 'similarity' && (
        <>
          <div className="evaluator-params__field">
            <label htmlFor={fieldId(idPrefix, 'embedding-model')}>Embedding model name</label>
            <select
              id={fieldId(idPrefix, 'embedding-model')}
              className="evaluator-params__input"
              value={value.embedding_model_name || SIMILARITY_EMBEDDING_OPTIONS[0]}
              onChange={(e) => patch({ embedding_model_name: e.target.value })}
              disabled={disabled}
            >
              {SIMILARITY_EMBEDDING_OPTIONS.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>
          <div className="evaluator-params__field">
            <label htmlFor={fieldId(idPrefix, 'metric-id')}>Metric ID</label>
            <select
              id={fieldId(idPrefix, 'metric-id')}
              className="evaluator-params__input"
              value={
                LLM_JUDGE_METRIC_OPTIONS.includes(value.metric_id as LlmJudgeMetricId)
                  ? value.metric_id
                  : 'quality'
              }
              onChange={(e) => patch({ metric_id: e.target.value })}
              disabled={disabled}
            >
              {LLM_JUDGE_METRIC_OPTIONS.map((metric) => (
                <option key={metric} value={metric}>
                  {LLM_JUDGE_METRIC_LABELS[metric]}
                </option>
              ))}
            </select>
          </div>
          {includeDirection ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'direction')}>Selection direction</label>
              <select
                id={fieldId(idPrefix, 'direction')}
                className="evaluator-params__input"
                value={value.direction}
                onChange={(e) => patch({ direction: e.target.value as EvaluationDirection })}
                disabled={disabled}
              >
                <option value="maximize">Maximize (higher score wins)</option>
                <option value="minimize">Minimize (lower score wins)</option>
              </select>
              <p className="evaluator-params__hint">
                Used when selecting the best candidate during a tuning run.
              </p>
            </div>
          ) : null}
          {includeDirection && (value.metric_id === 'token' || value.metric_id === 'cost') ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'quality-drop-pct')}>Allowed quality drop (%)</label>
              <input
                id={fieldId(idPrefix, 'quality-drop-pct')}
                type="number"
                min={1}
                max={100}
                step={1}
                className="evaluator-params__input"
                value={value.quality_drop_pct}
                onChange={(e) => patch({ quality_drop_pct: e.target.value })}
                disabled={disabled}
              />
              <p className="evaluator-params__hint">
                How much quality is allowed to drop while selecting by {LLM_JUDGE_METRIC_LABELS[value.metric_id as LlmJudgeMetricId] ?? value.metric_id}.
                100 means quality is not held back at all — the optimizer will pick the cheapest prompt regardless of how bad it is.
              </p>
            </div>
          ) : null}
        </>
      )}

      {mode === 'bert_score' && (
        <>
          <div className="evaluator-params__field">
            <label htmlFor={fieldId(idPrefix, 'metric-to-use')}>Metric to use</label>
            <select
              id={fieldId(idPrefix, 'metric-to-use')}
              className="evaluator-params__input"
              value={
                BERT_SCORE_METRIC_OPTIONS.includes(value.metric_to_use as BertScoreMetric)
                  ? value.metric_to_use
                  : 'F1'
              }
              onChange={(e) => patch({ metric_to_use: e.target.value })}
              disabled={disabled}
            >
              {BERT_SCORE_METRIC_OPTIONS.map((metric) => (
                <option key={metric} value={metric}>
                  {BERT_SCORE_METRIC_LABELS[metric]}
                </option>
              ))}
            </select>
          </div>
          <div className="evaluator-params__grid">
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'lang')}>Language</label>
              <select
                id={fieldId(idPrefix, 'lang')}
                className="evaluator-params__input"
                value={
                  BERT_SCORE_LANG_OPTIONS.includes(value.lang as BertScoreLang)
                    ? value.lang
                    : 'en'
                }
                onChange={(e) => patch({ lang: e.target.value })}
                disabled={disabled}
              >
                {BERT_SCORE_LANG_OPTIONS.map((lang) => (
                  <option key={lang} value={lang}>
                    {BERT_SCORE_LANG_LABELS[lang]}
                  </option>
                ))}
              </select>
              <p className="evaluator-params__hint">
                Must match the dataset language and be supported by BERTScore.
              </p>
            </div>
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'model-type')}>Model type</label>
              <select
                id={fieldId(idPrefix, 'model-type')}
                className="evaluator-params__input"
                value={
                  (BERT_SCORE_MODEL_OPTIONS as readonly string[]).includes(value.model_type)
                    ? value.model_type
                    : BERT_SCORE_MODEL_OPTIONS[0]
                }
                onChange={(e) => patch({ model_type: e.target.value })}
                disabled={disabled}
              >
                {BERT_SCORE_MODEL_OPTIONS.map((model) => (
                  <option key={model} value={model}>
                    {BERT_SCORE_MODEL_LABELS[model as BertScoreModelType]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="evaluator-params__field">
            <label htmlFor={fieldId(idPrefix, 'metric-id')}>Metric ID</label>
            <select
              id={fieldId(idPrefix, 'metric-id')}
              className="evaluator-params__input"
              value={
                LLM_JUDGE_METRIC_OPTIONS.includes(value.metric_id as LlmJudgeMetricId)
                  ? value.metric_id
                  : 'quality'
              }
              onChange={(e) => patch({ metric_id: e.target.value })}
              disabled={disabled}
            >
              {LLM_JUDGE_METRIC_OPTIONS.map((metric) => (
                <option key={metric} value={metric}>
                  {LLM_JUDGE_METRIC_LABELS[metric]}
                </option>
              ))}
            </select>
          </div>
          {includeDirection ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'direction')}>Selection direction</label>
              <select
                id={fieldId(idPrefix, 'direction')}
                className="evaluator-params__input"
                value={value.direction}
                onChange={(e) => patch({ direction: e.target.value as EvaluationDirection })}
                disabled={disabled}
              >
                <option value="maximize">Maximize (higher score wins)</option>
                <option value="minimize">Minimize (lower score wins)</option>
              </select>
              <p className="evaluator-params__hint">
                Used when selecting the best candidate during a tuning run.
              </p>
            </div>
          ) : null}
          {includeDirection && (value.metric_id === 'token' || value.metric_id === 'cost') ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'quality-drop-pct')}>Allowed quality drop (%)</label>
              <input
                id={fieldId(idPrefix, 'quality-drop-pct')}
                type="number"
                min={1}
                max={100}
                step={1}
                className="evaluator-params__input"
                value={value.quality_drop_pct}
                onChange={(e) => patch({ quality_drop_pct: e.target.value })}
                disabled={disabled}
              />
              <p className="evaluator-params__hint">
                How much quality is allowed to drop while selecting by {LLM_JUDGE_METRIC_LABELS[value.metric_id as LlmJudgeMetricId] ?? value.metric_id}.
                100 means quality is not held back at all — the optimizer will pick the cheapest prompt regardless of how bad it is.
              </p>
            </div>
          ) : null}
        </>
      )}

      {mode === 'openai-similarity' && (
        <>
          <div className="evaluator-params__field">
            <label htmlFor={fieldId(idPrefix, 'embedding-model')}>Embedding model name</label>
            <select
              id={fieldId(idPrefix, 'embedding-model')}
              className="evaluator-params__input"
              value={value.embedding_model_name || OPENAI_SIMILARITY_EMBEDDING_OPTIONS[0]}
              onChange={(e) => patch({ embedding_model_name: e.target.value })}
              disabled={disabled}
            >
              {OPENAI_SIMILARITY_EMBEDDING_OPTIONS.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>
          <div className="evaluator-params__field">
            <label htmlFor={fieldId(idPrefix, 'metric-id')}>Metric ID</label>
            <select
              id={fieldId(idPrefix, 'metric-id')}
              className="evaluator-params__input"
              value={
                LLM_JUDGE_METRIC_OPTIONS.includes(value.metric_id as LlmJudgeMetricId)
                  ? value.metric_id
                  : 'quality'
              }
              onChange={(e) => patch({ metric_id: e.target.value })}
              disabled={disabled}
            >
              {LLM_JUDGE_METRIC_OPTIONS.map((metric) => (
                <option key={metric} value={metric}>
                  {LLM_JUDGE_METRIC_LABELS[metric]}
                </option>
              ))}
            </select>
          </div>
          {includeDirection ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'direction')}>Selection direction</label>
              <select
                id={fieldId(idPrefix, 'direction')}
                className="evaluator-params__input"
                value={value.direction}
                onChange={(e) => patch({ direction: e.target.value as EvaluationDirection })}
                disabled={disabled}
              >
                <option value="maximize">Maximize (higher score wins)</option>
                <option value="minimize">Minimize (lower score wins)</option>
              </select>
              <p className="evaluator-params__hint">
                Used when selecting the best candidate during a tuning run.
              </p>
            </div>
          ) : null}
          {includeDirection && (value.metric_id === 'token' || value.metric_id === 'cost') ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'quality-drop-pct')}>Allowed quality drop (%)</label>
              <input
                id={fieldId(idPrefix, 'quality-drop-pct')}
                type="number"
                min={1}
                max={100}
                step={1}
                className="evaluator-params__input"
                value={value.quality_drop_pct}
                onChange={(e) => patch({ quality_drop_pct: e.target.value })}
                disabled={disabled}
              />
              <p className="evaluator-params__hint">
                How much quality is allowed to drop while selecting by {LLM_JUDGE_METRIC_LABELS[value.metric_id as LlmJudgeMetricId] ?? value.metric_id}.
                100 means quality is not held back at all — the optimizer will pick the cheapest prompt regardless of how bad it is.
              </p>
            </div>
          ) : null}
        </>
      )}

      {mode === 'llm-judge' && (
        <>
          <div className="evaluator-params__field">
            <label htmlFor={fieldId(idPrefix, 'prompt')}>
              Judge prompt <span className="evaluator-params__required">*</span>
            </label>
            <textarea
              id={fieldId(idPrefix, 'prompt')}
              className="evaluator-params__textarea"
              value={value.prompt}
              onChange={(e) => patch({ prompt: e.target.value })}
              disabled={disabled}
              rows={4}
              placeholder="Rubric for the judge LLM — required for llm-judge mode"
            />
          </div>
          <div className="evaluator-params__grid">
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'scope')}>Evaluation scope</label>
              <select
                id={fieldId(idPrefix, 'scope')}
                className="evaluator-params__input"
                value={value.evaluation_scope}
                onChange={(e) => patch({ evaluation_scope: e.target.value as EvaluationScope })}
                disabled={disabled}
              >
                <option value="message">Message</option>
                <option value="dialog">Dialog</option>
              </select>
            </div>
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'metric-id')}>Metric ID</label>
              <select
                id={fieldId(idPrefix, 'metric-id')}
                className="evaluator-params__input"
                value={
                  LLM_JUDGE_METRIC_OPTIONS.includes(value.metric_id as LlmJudgeMetricId)
                    ? value.metric_id
                    : 'quality'
                }
                onChange={(e) => patch({ metric_id: e.target.value })}
                disabled={disabled}
              >
                {LLM_JUDGE_METRIC_OPTIONS.map((metric) => (
                  <option key={metric} value={metric}>
                    {LLM_JUDGE_METRIC_LABELS[metric]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {includeDirection ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'direction')}>Selection direction</label>
              <select
                id={fieldId(idPrefix, 'direction')}
                className="evaluator-params__input"
                value={value.direction}
                onChange={(e) => patch({ direction: e.target.value as EvaluationDirection })}
                disabled={disabled}
              >
                <option value="maximize">Maximize (higher score wins)</option>
                <option value="minimize">Minimize (lower score wins)</option>
              </select>
              <p className="evaluator-params__hint">
                Used when selecting the best candidate during a tuning run.
              </p>
            </div>
          ) : null}
          {includeDirection && (value.metric_id === 'token' || value.metric_id === 'cost') ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'quality-drop-pct')}>Allowed quality drop (%)</label>
              <input
                id={fieldId(idPrefix, 'quality-drop-pct')}
                type="number"
                min={1}
                max={100}
                step={1}
                className="evaluator-params__input"
                value={value.quality_drop_pct}
                onChange={(e) => patch({ quality_drop_pct: e.target.value })}
                disabled={disabled}
              />
              <p className="evaluator-params__hint">
                How much quality is allowed to drop while selecting by {LLM_JUDGE_METRIC_LABELS[value.metric_id as LlmJudgeMetricId] ?? value.metric_id}.
                100 means quality is not held back at all — the optimizer will pick the cheapest prompt regardless of how bad it is.
              </p>
            </div>
          ) : null}
          <div className="evaluator-params__subsection">
            <h5 className="evaluator-params__subtitle">Judge LLM</h5>
            <div className="evaluator-params__field evaluator-params__temperature">
              <label htmlFor={fieldId(idPrefix, 'judge-temp')}>Temperature</label>
              <div className="evaluator-params__range">
                <input
                  id={fieldId(idPrefix, 'judge-temp')}
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={judgeTemperature}
                  disabled={disabled}
                  onChange={(e) => patch({ judge_temperature: parseFloat(e.target.value) })}
                />
                <div className="evaluator-params__range-value">
                  <span>{judgeTemperature.toFixed(1)}</span>
                </div>
              </div>
            </div>
            <div className="evaluator-params__grid">
              <div className="evaluator-params__field">
                <label htmlFor={fieldId(idPrefix, 'judge-max-tokens')}>Max tokens</label>
                <input
                  id={fieldId(idPrefix, 'judge-max-tokens')}
                  type="text"
                  inputMode="numeric"
                  className="evaluator-params__input"
                  value={value.judge_max_tokens}
                  onChange={(e) => patch({ judge_max_tokens: e.target.value })}
                  disabled={disabled}
                />
              </div>
              <div className="evaluator-params__field">
                <label htmlFor={fieldId(idPrefix, 'judge-model')}>Judge model</label>
                <select
                  id={fieldId(idPrefix, 'judge-model')}
                  className="evaluator-params__input"
                  value={value.judge_model_name || modelOptions[0] || ''}
                  onChange={(e) => patch({ judge_model_name: e.target.value })}
                  disabled={disabled}
                >
                  {modelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </>
      )}

      {mode === 'algorithmic' && (
        <>
          <div className="evaluator-params__grid">
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'scope')}>Evaluation scope</label>
              <select
                id={fieldId(idPrefix, 'scope')}
                className="evaluator-params__input"
                value={value.evaluation_scope}
                onChange={(e) => patch({ evaluation_scope: e.target.value as EvaluationScope })}
                disabled={disabled}
              >
                <option value="message">Message</option>
                <option value="dialog">Dialog</option>
              </select>
            </div>
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'metric-id')}>Metric ID</label>
              <select
                id={fieldId(idPrefix, 'metric-id')}
                className="evaluator-params__input"
                value={
                  ALGORITHMIC_METRIC_OPTIONS.includes(value.metric_id as AlgorithmicMetricId)
                    ? value.metric_id
                    : ALGORITHMIC_METRIC_OPTIONS[0]
                }
                onChange={(e) => patch({ metric_id: e.target.value })}
                disabled={disabled}
              >
                {ALGORITHMIC_METRIC_OPTIONS.map((metric) => (
                  <option key={metric} value={metric}>
                    {ALGORITHMIC_METRIC_LABELS[metric]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="evaluator-params__grid">
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'unit')}>Unit</label>
              <select
                id={fieldId(idPrefix, 'unit')}
                className="evaluator-params__input"
                value={value.unit}
                onChange={(e) => patch({ unit: e.target.value as AlgorithmicUnit })}
                disabled={disabled}
              >
                <option value="tokens">Tokens</option>
                <option value="words">Words</option>
                <option value="characters">Characters</option>
                <option value="message_count">Message count</option>
              </select>
            </div>
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'aggregation')}>Aggregation</label>
              <select
                id={fieldId(idPrefix, 'aggregation')}
                className="evaluator-params__input"
                value={value.aggregation}
                onChange={(e) => patch({ aggregation: e.target.value as AlgorithmicAggregation })}
                disabled={disabled}
              >
                <option value="mean">Mean</option>
                <option value="min">Min</option>
                <option value="max">Max</option>
                <option value="sum">Sum</option>
              </select>
            </div>
          </div>
          {includeDirection ? (
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'direction')}>Selection direction</label>
              <select
                id={fieldId(idPrefix, 'direction')}
                className="evaluator-params__input"
                value={value.direction}
                onChange={(e) => patch({ direction: e.target.value as EvaluationDirection })}
                disabled={disabled}
              >
                <option value="maximize">Maximize (higher score wins)</option>
                <option value="minimize">Minimize (lower score wins)</option>
              </select>
            </div>
          ) : null}
          <div className="evaluator-params__grid">
            {value.unit === 'tokens' ? (
              <div className="evaluator-params__field">
                <label htmlFor={fieldId(idPrefix, 'token-model')}>Token model</label>
                <select
                  id={fieldId(idPrefix, 'token-model')}
                  className="evaluator-params__input"
                  value={value.token_model_name || modelOptions[0] || ''}
                  onChange={(e) => patch({ token_model_name: e.target.value })}
                  disabled={disabled}
                >
                  {modelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
                <p className="evaluator-params__hint">
                  Tokenizer model used to count tokens in responses.
                </p>
              </div>
            ) : null}
            <div className="evaluator-params__field">
              <label htmlFor={fieldId(idPrefix, 'normalization-max')}>Normalization max</label>
              <input
                id={fieldId(idPrefix, 'normalization-max')}
                type="text"
                inputMode="numeric"
                className="evaluator-params__input"
                value={value.normalization_max}
                onChange={(e) => patch({ normalization_max: e.target.value })}
                disabled={disabled}
                placeholder="500"
              />
            </div>
          </div>
          <p className="evaluator-params__hint">
            Score = min(max(raw_value, 0) / normalization_max, 1). Direction applies only to config
            tuning candidate selection.
          </p>
        </>
      )}

    </div>
  );
};

export default EvaluatorParamsFields;
